import { useState, useCallback } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ThreadItem } from './ThreadItem';
import { useConversationThreads } from '@/hooks/useOmniChannel';
import { cn } from '@/lib/utils';
import type { ConversationThread } from '@/types/omniChannel';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

interface ThreadListProps {
  selectedThreadId: string | null;
  onSelectThread: (thread: ConversationThread) => void;
}

export function ThreadList({ selectedThreadId, onSelectThread }: ThreadListProps) {
  const [search, setSearch] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const debouncedSearch = useDebouncedValue(search, 300);

  const { data: threads, isLoading } = useConversationThreads({
    search: debouncedSearch,
    unreadOnly,
  });

  const handleThreadClick = useCallback((thread: ConversationThread) => {
    onSelectThread(thread);
  }, [onSelectThread]);

  const unreadThreadCount = threads?.filter((t) => t.unread_count > 0).length;

  const chipClass = (active: boolean) =>
    cn(
      'inline-flex h-7 items-center rounded-full border px-3 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      active
        ? 'border-transparent bg-primary/10 text-primary'
        : 'border-border bg-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground'
    );

  return (
    <div className="flex flex-col h-full border-r border-border">
      {/* Search and filter header */}
      <div className="p-3 border-b border-border space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search conversations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setUnreadOnly(false)}
            className={chipClass(!unreadOnly)}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setUnreadOnly(true)}
            className={chipClass(unreadOnly)}
          >
            Unread
            {unreadThreadCount != null && unreadThreadCount > 0 && ` · ${unreadThreadCount}`}
          </button>
        </div>
      </div>

      {/* Thread list */}
      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : threads?.length === 0 ? (
          <div className="text-center py-12 px-4 text-muted-foreground">
            <p className="text-sm">No conversations found</p>
            {search && (
              <p className="text-xs mt-1">Try adjusting your search</p>
            )}
          </div>
        ) : (
          <div>
            {threads?.map((thread) => (
              <ThreadItem
                key={thread.id}
                thread={thread}
                isSelected={selectedThreadId === thread.id}
                onClick={() => handleThreadClick(thread)}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
